import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLatest } from '@/shared/hooks/useLatest';
import { Button } from '@/shared/ui/button';
import { FieldLabel } from '@/shared/ui/form-field/FieldLabel';
import { InlineFieldLabel } from '@/shared/ui/form-field/InlineFieldLabel';
import { FieldMessage } from '@/shared/ui/form-field/FieldMessage';
import {
  DEFAULT_LIST_PICKER_MODE,
  DEFAULT_PICK_MODAL_WIDTH,
  DEFAULT_SELECTOR_PAGE_SIZE,
} from '../constants';
import { AsyncSelectorPickModal } from '../pick-modal/AsyncSelectorPickModal';
import { usePagedAsyncSuggestions } from '../search/usePagedAsyncSuggestions';
import { AsyncSelectorField } from './AsyncSelectorField';
import { AsyncSelectorPopover } from './AsyncSelectorPopover';
import type { AsyncSelectorProps } from './AsyncSelector.types';
import { useAsyncSelectorLegacyNav } from './hooks/useAsyncSelectorLegacyNav';
import { useAsyncSelectorPopover } from './hooks/useAsyncSelectorPopover';
import { useAsyncSelectorSelection } from './hooks/useAsyncSelectorSelection';

/**
 * Selector dle designového handoffu „Anatomie selectoru": vybrané záznamy jako
 * plnohodnotné řádky uvnitř pole, vyhledávání v popoveru, akce + (nový záznam,
 * jen prázdné pole) a ⋯ (vždy; otevírá list page dané object category ve
 * výběrovém režimu), řádkové akce ✎/× a centrální × pro multi se ≥ 2 záznamy.
 */
export function AsyncSelector<TItem>({
  mode,
  label,
  helperText,
  error,
  selected,
  getKey,
  columns,
  renderSelected,
  renderSelectedSecondary,
  isItemSelected,
  search,
  queryKey,
  onChange,
  placeholder,
  testId,
  required,
  layout = 'stacked',
  labelWidth = 104,
  objectCategory,
  form,
  resolveByIds,
  listPicker = DEFAULT_LIST_PICKER_MODE,
  pickModalTitle,
  pickModalWidth = DEFAULT_PICK_MODAL_WIDTH,
  renderPickToolbar,
  defaultPageSize = DEFAULT_SELECTOR_PAGE_SIZE,
  filterResetKey,
  trigger = 'field',
  buttonLabel,
  buttonVariant = 'default',
  confirmLabel,
  lockedIds,
  addMode = false,
}: AsyncSelectorProps<TItem>) {
  const { t } = useTranslation('asyncSelector');
  const fieldId = useId();
  const popupId = useId();

  const isButton = trigger === 'button';
  // button + multi drží rozpracovaný (draft) výběr a potvrzuje ho tlačítkem „Vybrat".
  const workingMode = isButton && mode === 'multi';

  const popover = useAsyncSelectorPopover();
  const suggestions = usePagedAsyncSuggestions<TItem>({
    queryKey,
    search,
    enabled: popover.pickerActive,
    initialQuery: '',
    initialPage: 1,
    initialPageSize: defaultPageSize,
    filterResetKey,
  });

  const selection = useAsyncSelectorSelection({
    mode,
    selected,
    getKey,
    onChange,
    isItemSelected,
    resetSuggestions: suggestions.reset,
    closePopover: () => popover.setOpen(false),
  });

  const nav = useAsyncSelectorLegacyNav({
    objectCategory,
    form,
    listPicker,
    mode,
    selected,
    getKey,
    onChange,
    resolveByIds,
    openPickModal: () => popover.setPickModalOpen(true),
    resetSuggestions: suggestions.reset,
  });

  const onChangeRef = useLatest(onChange);

  const [activeIdx, setActiveIdx] = useState(0);
  // button + multi: rozpracovaný draft výběr (čistí se při otevření, potvrzuje „Vybrat").
  const [working, setWorking] = useState<readonly TItem[]>([]);

  // Kotva (.phxsel) a popover (.phxsel-pop) pro viewport-aware měření.
  const anchorRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<{
    v: 'down' | 'up';
    h: 'left' | 'right';
    listMax: number;
  }>({ v: 'down', h: 'left', listMax: 320 });

  const resolvedButtonLabel = buttonLabel ?? t('button.defaultLabel');
  const resolvedConfirmLabel = confirmLabel ?? t('footer.confirm');

  const workingIds = useMemo(() => new Set(working.map(getKey)), [working, getKey]);
  // Zamčené řádky (už v cílovém seznamu) — jen mimo addMode.
  const isLocked = useCallback(
    (item: TItem) => !addMode && !!lockedIds && lockedIds.has(getKey(item)),
    [addMode, lockedIds, getKey]
  );
  // Zaškrtnutí řádku popoveru: v draft módu dle draftu, jinak dle skutečného výběru.
  const isRowSelected = useCallback(
    (item: TItem) => (workingMode ? workingIds.has(getKey(item)) : selection.isSelected(item)),
    [workingMode, workingIds, getKey, selection]
  );

  useEffect(() => {
    setActiveIdx(0);
  }, [suggestions.query, popover.open, suggestions.items]);

  // Fokus jde při otevření popoveru automaticky do vyhledávání.
  useEffect(() => {
    if (popover.open) popover.inputRef.current?.focus();
  }, [popover.open, popover.inputRef]);

  // Každé otevření draft popoveru začíná s prázdným výběrem.
  useEffect(() => {
    if (popover.open && workingMode) setWorking([]);
  }, [popover.open, workingMode]);

  // Viewport-aware umístění popoveru: překlopí nahoru / zarovná doprava a omezí
  // výšku seznamu tak, aby se popover vždy vešel. Měří se bez bliknutí
  // (useLayoutEffect) a přepočítává při scrollu / resize / změně obsahu.
  useLayoutEffect(() => {
    if (!popover.open) return;
    const measure = () => {
      const anchor = anchorRef.current;
      const pop = popRef.current;
      if (!anchor || !pop) return;
      const rect = anchor.getBoundingClientRect();
      const popH = pop.offsetHeight;
      const popW = pop.offsetWidth;
      const m = 8;
      const below = window.innerHeight - rect.bottom;
      const above = rect.top;
      const v: 'down' | 'up' = below < popH + m && above > below ? 'up' : 'down';
      const avail = (v === 'up' ? above : below) - m * 2;
      const listMax = Math.max(132, Math.min(320, avail - 112));
      const h: 'left' | 'right' =
        rect.left + popW > window.innerWidth - m && rect.right - popW > m ? 'right' : 'left';
      setPlacement((prev) =>
        prev.v === v && prev.h === h && prev.listMax === listMax ? prev : { v, h, listMax }
      );
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [popover.open, suggestions.query, suggestions.page, suggestions.items.length]);

  const totalPages = Math.max(1, Math.ceil(suggestions.totalCount / suggestions.pageSize));

  // Klik/Enter na řádek popoveru. Pole: single vybere (nahradí) a zavře, multi přepne.
  // Tlačítko: draft (multi) přepíná rozpracovaný výběr; single emituje dávku jednoho
  // záznamu — v addMode resetuje a nechá popover otevřený, jinak zavře.
  const pickItem = useCallback(
    (item: TItem) => {
      if (isLocked(item)) return;
      if (workingMode) {
        const key = getKey(item);
        setWorking((prev) =>
          prev.some((existing) => getKey(existing) === key)
            ? prev.filter((existing) => getKey(existing) !== key)
            : [...prev, item]
        );
        return;
      }
      if (isButton) {
        onChange([item]);
        suggestions.reset();
        if (addMode) {
          setActiveIdx(0);
        } else {
          popover.setOpen(false);
        }
        return;
      }
      if (mode === 'multi') {
        selection.handleToggleSelection(item);
        return;
      }
      if (selection.isSelected(item)) {
        suggestions.reset();
        popover.setOpen(false);
        return;
      }
      selection.handleSelect(item);
    },
    [
      isLocked,
      workingMode,
      getKey,
      isButton,
      onChange,
      addMode,
      mode,
      selection,
      suggestions,
      popover,
    ]
  );

  // Potvrzení draftu (button + multi): propíše dávku rodiči. V addMode vyprázdní draft
  // a nechá popover otevřený pro další dávku, jinak zavře.
  const confirmWorking = useCallback(() => {
    onChange(working);
    suggestions.reset();
    if (addMode) {
      setWorking([]);
      setActiveIdx(0);
    } else {
      popover.setOpen(false);
    }
  }, [onChange, working, suggestions, addMode, popover]);

  const handleSearchKey = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const items = suggestions.items;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIdx((idx) => Math.min(items.length - 1, idx + 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIdx((idx) => Math.max(0, idx - 1));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const item = items[activeIdx];
        if (item) pickItem(item);
      } else if (event.key === 'Escape') {
        popover.setOpen(false);
      } else if (event.key === 'PageDown') {
        event.preventDefault();
        suggestions.setPage(Math.min(totalPages, suggestions.page + 1));
      } else if (event.key === 'PageUp') {
        event.preventDefault();
        suggestions.setPage(Math.max(1, suggestions.page - 1));
      } else if (
        event.key === 'Backspace' &&
        suggestions.query.length === 0 &&
        mode === 'multi' &&
        !isButton &&
        selected.length > 0
      ) {
        selection.handleRemove(selected[selected.length - 1]!);
      }
    },
    [suggestions, activeIdx, pickItem, popover, totalPages, mode, isButton, selected, selection]
  );

  const handleFieldKey = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!popover.open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
        event.preventDefault();
        popover.setOpen(true);
        return;
      }
      if (popover.open && event.key === 'Escape') {
        popover.setOpen(false);
      }
    },
    [popover]
  );

  const hasError = !!error;
  const messageId = `${fieldId}-message`;
  const isEmpty = selected.length === 0;
  // + Nový záznam — jen v prázdném stavu; ⋯ Otevřít seznam — vždy.
  const showCreateBtn = nav.hasNavigation && isEmpty;
  const showListBtn = nav.hasNavigation;

  const isInline = layout === 'inline';

  return (
    <div
      className={isInline ? 'phx-field gap-phx-4 flex min-w-0 items-start' : 'grid gap-1.5'}
      data-layout={isInline ? 'inline' : undefined}
      data-testid={testId}
      ref={popover.wrapRef}
    >
      {isInline ? (
        <InlineFieldLabel htmlFor={fieldId} width={labelWidth}>
          {label}
        </InlineFieldLabel>
      ) : (
        <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
      )}

      <div className={isInline ? 'flex min-w-0 flex-1 flex-col gap-1.5' : 'contents'}>
        <div
          className="phxsel"
          ref={anchorRef}
          {...(isButton ? { 'data-trigger': 'button' } : { 'data-layout': 'rows' })}
          {...(popover.open ? { 'data-open': '' } : {})}
          {...(mode === 'multi' ? { 'data-multi': '' } : {})}
          {...(!isEmpty && !isButton ? { 'data-filled': '' } : {})}
          {...(required ? { 'data-required': '' } : {})}
          {...(hasError ? { 'data-error': '' } : {})}
        >
          {isButton ? (
            <Button
              variant={buttonVariant}
              className="phxsel-trigger"
              iconLeft="plus"
              aria-haspopup="listbox"
              aria-expanded={popover.open}
              aria-controls={popupId}
              {...(popover.open ? { 'data-open': '' } : {})}
              onClick={() => popover.setOpen(!popover.open)}
            >
              {resolvedButtonLabel}
            </Button>
          ) : (
            <AsyncSelectorField
              mode={mode}
              fieldId={fieldId}
              popupId={popupId}
              open={popover.open}
              selected={selected}
              columns={columns}
              renderSelected={renderSelected}
              renderSelectedSecondary={renderSelectedSecondary}
              getKey={getKey}
              placeholder={placeholder ?? t('placeholder')}
              isPending={nav.isPending}
              showCreateBtn={showCreateBtn}
              showListBtn={showListBtn}
              onActivate={() => popover.setOpen(true)}
              onKeyDown={handleFieldKey}
              onCreate={nav.handleCreate}
              onOpenList={nav.handleOpenList}
              onEditItem={nav.hasNavigation ? nav.handleEditItem : undefined}
              onRemove={selection.handleRemove}
              onClearAll={selection.handleClear}
              createLabel={t('actions.create')}
              openListLabel={t('actions.openList')}
              editLabel={t('actions.edit')}
              removeLabel={t('actions.remove')}
              clearAllLabel={t('actions.clearAll')}
              ariaLabel={label}
              ariaDescribedBy={helperText || error ? messageId : undefined}
              ariaInvalid={hasError}
            />
          )}

          {popover.open ? (
            <AsyncSelectorPopover
              mode={mode}
              popupId={popupId}
              columns={columns}
              suggestions={suggestions}
              getKey={getKey}
              isSelected={isRowSelected}
              isLocked={isLocked}
              selectedCount={selected.length}
              activeIdx={activeIdx}
              onHoverRow={setActiveIdx}
              onPickRow={pickItem}
              searchRef={popover.inputRef}
              onSearchKeyDown={handleSearchKey}
              placeholder={placeholder ?? t('placeholder')}
              searchAriaLabel={label}
              onEditItem={nav.hasNavigation ? nav.handleEditItem : undefined}
              editLabel={t('actions.edit')}
              editDisabled={nav.isPending}
              popRef={popRef}
              vpos={placement.v}
              halign={placement.h}
              listMaxHeight={placement.listMax}
              isButton={isButton}
              onCreate={isButton && nav.hasNavigation ? nav.handleCreate : undefined}
              onOpenList={
                isButton && nav.hasNavigation
                  ? () => {
                      popover.setOpen(false);
                      nav.handleOpenList();
                    }
                  : undefined
              }
              createLabel={t('actions.create')}
              openListLabel={t('actions.openList')}
              showConfirm={workingMode}
              confirmLabel={resolvedConfirmLabel}
              confirmCount={working.length}
              onConfirm={confirmWorking}
            />
          ) : null}
        </div>

        {typeof listPicker === 'object' ? (
          // ⋯ otevírá plnohodnotnou Phoenix ListPage ve výběrovém režimu; potvrzená id se
          // přeloží na záznamy přes resolveByIds (kruh selector ↔ listpage).
          <listPicker.listPage
            open={popover.pickModalOpen}
            onClose={() => popover.setPickModalOpen(false)}
            initialSelectedIds={selected.map(getKey)}
            onConfirm={(ids) => {
              popover.setPickModalOpen(false);
              if (!resolveByIds) return;
              void resolveByIds(ids)
                .then((items) => onChangeRef.current(mode === 'single' ? items.slice(0, 1) : items))
                .catch(() => undefined);
            }}
          />
        ) : (
          <AsyncSelectorPickModal
            open={popover.pickModalOpen}
            onClose={() => popover.setPickModalOpen(false)}
            initialSelected={selected}
            mode={mode}
            columns={columns}
            suggestions={suggestions}
            onConfirm={(items) => {
              onChangeRef.current(items);
              popover.setPickModalOpen(false);
            }}
            renderPickToolbar={renderPickToolbar}
            title={pickModalTitle}
            width={pickModalWidth}
            getKey={getKey}
          />
        )}

        {error || helperText ? (
          <FieldMessage id={messageId} variant={hasError ? 'error' : 'helper'}>
            {error ?? helperText}
          </FieldMessage>
        ) : null}
      </div>
    </div>
  );
}
